import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Square, Activity, Compass, TrendingDown, Gauge, Zap, RotateCcw, History, Award, Info, Trash2, Camera, MapPin, Share2 } from 'lucide-react';
import { Storage } from '../lib/storage';
import { supabase } from '../lib/supabase';
import html2canvas from 'html2canvas';

interface RideSession {
  id: string;
  fecha: string;
  tiempo: string;
  velMax: number;
  tumbadaMaxIzq: number;
  tumbadaMaxDer: number;
  gForceMax: number;
  descensoMetros: number;
  distancia: number;
}

// Fórmula de Haversine para calcular distancia por coordenadas GPS ($0 Costo)
function getDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Radio de la Tierra en km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distancia en kilómetros
}

export const getRankConfig = (session: RideSession | null) => {
  if (!session) return {
    irg: 0,
    bg: 'bg-black',
    border: 'border-white/5',
    shadow: '',
    title: '¡RUTA CONQUISTADA! 🏁',
    titleColor: 'text-white',
    badgeBg: 'bg-slate-800 text-slate-400',
    rankName: 'PILOTO',
    accentColor: 'text-primary-cyan',
    accentBorder: 'border-primary-cyan/20',
    accentBg: 'bg-primary-cyan/10',
    barColorLeft: 'bg-red-500',
    barColorRight: 'bg-green-500',
  };

  const velMax = session.velMax || 0;
  const maxTumbada = Math.max(session.tumbadaMaxIzq || 0, session.tumbadaMaxDer || 0);
  const gForceMax = session.gForceMax || 1.0;

  // Calcular IRG de manera Exponencial (Dificultad Profesional Competitiva)
  const velScore = Math.min(40, Math.pow(velMax / 90, 2) * 40);
  const tumbadaScore = Math.min(35, Math.pow(maxTumbada / 45, 2) * 35);
  const gScore = Math.min(25, Math.pow(Math.max(0, gForceMax - 1) / 1.5, 2) * 25);
  const irg = Math.round(velScore + tumbadaScore + gScore);

  if (irg >= 90) {
    // CAMPEÓN (Oro, valla ultra-alta)
    return {
      irg,
      bg: 'bg-gradient-to-br from-[#0B0905] via-[#241703] to-[#050301]',
      border: 'border-2 border-amber-500/60',
      shadow: 'shadow-[0_0_40px_rgba(245,158,11,0.25)]',
      title: '🏆 ¡RÉCORD CAMPEÓN! 🏆',
      titleColor: 'text-amber-400 drop-shadow-[0_2px_4px_rgba(245,158,11,0.2)]',
      badgeBg: 'bg-amber-500/20 text-amber-300 border border-amber-500/30',
      rankName: 'CAMPEÓN',
      accentColor: 'text-amber-400',
      accentBorder: 'border-amber-500/30',
      accentBg: 'bg-amber-500/10',
      barColorLeft: 'bg-amber-600',
      barColorRight: 'bg-amber-400',
    };
  } else if (irg >= 80) {
    // SUBCAMPEÓN / PRO (Cian, valla muy alta)
    return {
      irg,
      bg: 'bg-gradient-to-br from-[#040912] via-[#0D1C30] to-[#020408]',
      border: 'border-2 border-primary-cyan/60',
      shadow: 'shadow-[0_0_30px_rgba(0,168,255,0.25)]',
      title: '🥈 ¡RÉCORD PRO! 🥈',
      titleColor: 'text-primary-cyan drop-shadow-[0_2px_4px_rgba(0,168,255,0.2)]',
      badgeBg: 'bg-primary-cyan/20 text-primary-cyan border border-primary-cyan/30',
      rankName: 'SUBCAMPEÓN',
      accentColor: 'text-primary-cyan',
      accentBorder: 'border-primary-cyan/30',
      accentBg: 'bg-primary-cyan/10',
      barColorLeft: 'bg-red-500',
      barColorRight: 'bg-green-500',
    };
  } else {
    // PILOTO (Rojo, rango por defecto para recorridos tranquilos)
    return {
      irg,
      bg: 'bg-gradient-to-br from-[#0A0505] via-[#160D0D] to-[#030101]',
      border: 'border border-red-500/40',
      shadow: 'shadow-[0_0_20px_rgba(239,68,68,0.15)]',
      title: '🥉 RUTA CONQUISTADA 🥉',
      titleColor: 'text-red-400',
      badgeBg: 'bg-red-500/20 text-red-300 border border-red-500/20',
      rankName: 'PILOTO',
      accentColor: 'text-red-400',
      accentBorder: 'border-red-500/20',
      accentBg: 'bg-red-500/10',
      barColorLeft: 'bg-red-600',
      barColorRight: 'bg-red-400',
    };
  }
};

const generateTelemetryImage = (session: any, rankCfg: any): Promise<{ blob: Blob, dataUrl: string }> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = 540;
    canvas.height = 700;
    const ctx = canvas.getContext('2d');
    if (!ctx) return reject(new Error("No canvas context"));

    // 1. Dibujar Gradiente de Fondo segun el rango (Gold, Cian o Rojo carbón, sincronizado con las nuevas dificultades)
    const grad = ctx.createLinearGradient(0, 0, 540, 700);
    if (rankCfg.irg >= 90) {
      grad.addColorStop(0, '#0B0905');
      grad.addColorStop(0.5, '#241703');
      grad.addColorStop(1, '#050301');
    } else if (rankCfg.irg >= 80) {
      grad.addColorStop(0, '#040912');
      grad.addColorStop(0.5, '#0D1C30');
      grad.addColorStop(1, '#020408');
    } else {
      grad.addColorStop(0, '#0A0505');
      grad.addColorStop(0.5, '#160D0D');
      grad.addColorStop(1, '#030101');
    }
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 540, 700);

    // 2. Dibujar borde exterior brillante
    ctx.strokeStyle = rankCfg.irg >= 90 
      ? 'rgba(245, 158, 11, 0.4)' 
      : rankCfg.irg >= 80 
        ? 'rgba(0, 168, 255, 0.4)' 
        : 'rgba(239, 68, 68, 0.3)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.roundRect(15, 15, 510, 670, 24);
    ctx.stroke();

    // 3. Franjas deportivas de F1 superiores (Rojo, Cian, Verde)
    ctx.fillStyle = '#ef4444'; // Rojo
    ctx.fillRect(40, 16, 153, 4);
    ctx.fillStyle = '#00a8ff'; // Cian
    ctx.fillRect(193, 16, 154, 4);
    ctx.fillStyle = '#22c55e'; // Verde
    ctx.fillRect(347, 16, 153, 4);

    // 4. Etiqueta superior
    ctx.textAlign = 'center';
    ctx.fillStyle = rankCfg.irg >= 90 ? '#f59e0b' : rankCfg.irg >= 80 ? '#00a8ff' : '#ef4444';
    ctx.font = 'bold 9px monospace';
    ctx.fillText('GRAVITY TELEMETRY', 270, 45);

    // 5. Título Principal de Rango
    ctx.fillStyle = '#ffffff';
    ctx.font = '900 24px Arial, sans-serif';
    ctx.fillText(rankCfg.title.toUpperCase(), 270, 85);

    // 6. Plaqueta/Cápsula de IRG (Aumentada y estilizada para máxima visibilidad)
    ctx.fillStyle = rankCfg.irg >= 90 ? 'rgba(245, 158, 11, 0.15)' : rankCfg.irg >= 80 ? 'rgba(0, 168, 255, 0.15)' : 'rgba(239, 68, 68, 0.15)';
    ctx.strokeStyle = rankCfg.irg >= 90 ? 'rgba(245, 158, 11, 0.3)' : rankCfg.irg >= 80 ? 'rgba(0, 168, 255, 0.3)' : 'rgba(239, 68, 68, 0.3)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.roundRect(150, 98, 240, 42, 21); // Más ancha, alta y con curvas perfectas
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = rankCfg.irg >= 90 ? '#f59e0b' : rankCfg.irg >= 80 ? '#00a8ff' : '#ef4444';
    ctx.font = '900 26px Arial, sans-serif'; // Aumentado a 26px para máxima visibilidad
    ctx.fillText(`IRG: ${rankCfg.irg} PTS`, 270, 127); // Ajustado verticalmente

    // 7. Método helper para dibujar cajitas de métricas
    const drawBox = (x: number, y: number, w: number, h: number, label: string, value: string, suffix: string, valColor: string) => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
      ctx.strokeStyle = rankCfg.irg >= 90 ? 'rgba(245, 158, 11, 0.15)' : rankCfg.irg >= 80 ? 'rgba(0, 168, 255, 0.15)' : 'rgba(239, 68, 68, 0.1)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, 16);
      ctx.fill();
      ctx.stroke();

      ctx.textAlign = 'center';
      ctx.fillStyle = '#64748b';
      ctx.font = '800 10px Arial, sans-serif';
      ctx.fillText(label, x + w / 2, y + 24);

      // Texto de valor (GIGANTE)
      ctx.fillStyle = valColor;
      ctx.font = '900 36px Arial, sans-serif';
      const valW = ctx.measureText(value).width;
      ctx.fillText(value, x + w / 2 - (suffix ? 18 : 0), y + 64);

      if (suffix) {
        ctx.fillStyle = rankCfg.irg >= 90 ? '#f59e0b' : rankCfg.irg >= 80 ? '#00a8ff' : '#64748b';
        ctx.font = 'bold 12px monospace';
        ctx.fillText(suffix, x + w / 2 + valW / 2 + 2 - (suffix ? 18 : 0), y + 62);
      }
    };

    const boxW = 220;
    const boxH = 80;
    const startY = 160;
    const gap = 15;

    // Velocidad Máxima
    drawBox(35, startY, boxW, boxH, 'VELOCIDAD MÁX', `${session.velMax}`, 'km/h', '#ffffff');
    // Tiempo Total
    drawBox(285, startY, boxW, boxH, 'TIEMPO TOTAL', `${session.tiempo}`, 'min', '#ffffff');
    // Descenso/Caída Vertical
    drawBox(35, startY + boxH + gap, boxW, boxH, 'CAÍDA VERTICAL', `-${session.descensoMetros}`, 'm', '#cbd5e1');
    // Fuerza G
    drawBox(285, startY + boxH + gap, boxW, boxH, 'FUERZA G MÁX', `${session.gForceMax}`, 'G', '#f59e0b');

    // 8. Balance de Tumbada Box
    const rollY = startY + (boxH + gap) * 2;
    const rollW = 470;
    const rollH = 175;

    ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
    ctx.strokeStyle = rankCfg.irg >= 90 ? 'rgba(245, 158, 11, 0.15)' : rankCfg.irg >= 80 ? 'rgba(0, 168, 255, 0.15)' : 'rgba(239, 68, 68, 0.15)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(35, rollY, rollW, rollH, 18);
    ctx.fill();
    ctx.stroke();

    ctx.textAlign = 'center';
    ctx.fillStyle = '#64748b';
    ctx.font = '800 9px Arial, sans-serif';
    ctx.fillText('BALANCE DE TUMBADA (ROLL)', 270, rollY + 25);

    // Ángulo Izquierdo (Rojo)
    ctx.textAlign = 'left';
    ctx.fillStyle = '#ef4444';
    ctx.font = '800 10px Arial, sans-serif';
    ctx.fillText('IZQ', 60, rollY + 45);
    ctx.fillStyle = '#ffffff';
    ctx.font = '900 24px Arial, sans-serif';
    ctx.fillText(`${session.tumbadaMaxIzq}°`, 60, rollY + 74);

    // Ángulo Derecho (Verde)
    ctx.textAlign = 'right';
    ctx.fillStyle = '#22c55e';
    ctx.font = '800 10px Arial, sans-serif';
    ctx.fillText('DER', 480, rollY + 45);
    ctx.fillStyle = '#ffffff';
    ctx.font = '900 24px Arial, sans-serif';
    ctx.fillText(`${session.tumbadaMaxDer}°`, 480, rollY + 74);

    // Barra de balance de inclinación
    const barX = 130;
    const barY = rollY + 54;
    const barW = 280;
    const barH = 8;
    ctx.fillStyle = '#1e293b';
    ctx.beginPath();
    ctx.roundRect(barX, barY, barW, barH, 4);
    ctx.fill();

    // Relleno Izquierdo
    const fillColorL = rankCfg.irg >= 90 ? '#f59e0b' : rankCfg.irg >= 80 ? '#00a8ff' : '#ef4444';
    const fillLW = (Math.min(50, (session.tumbadaMaxIzq / 90) * 50) / 100) * barW;
    ctx.fillStyle = fillColorL;
    ctx.beginPath();
    ctx.roundRect(barX + (barW / 2) - fillLW, barY, fillLW, barH, 3);
    ctx.fill();

    // Relleno Derecho
    const fillColorR = rankCfg.irg >= 90 ? '#f59e0b' : rankCfg.irg >= 80 ? '#00a8ff' : '#ef4444';
    const fillRW = (Math.min(50, (session.tumbadaMaxDer / 90) * 50) / 100) * barW;
    ctx.fillStyle = fillColorR;
    ctx.beginPath();
    ctx.roundRect(barX + (barW / 2), barY, fillRW, barH, 3);
    ctx.fill();

    // Distancia GPS
    ctx.textAlign = 'left';
    ctx.fillStyle = '#64748b';
    ctx.font = '800 10px Arial, sans-serif';
    ctx.fillText('⚡ DISTANCIA GPS:', 60, rollY + 125);

    ctx.textAlign = 'right';
    ctx.fillStyle = '#ffffff';
    ctx.font = '900 12px monospace';
    ctx.fillText(`${session.distancia || 0} km`, 480, rollY + 125);

    // 9. Pie de firma o sello oficial
    ctx.textAlign = 'center';
    ctx.fillStyle = '#334155';
    ctx.font = 'bold 7px monospace';
    ctx.fillText('CERTIFICADO EN COCKPIT DE TELEMETRÍA - GRAVITY BIKE RED', 270, 640);
    ctx.font = '800 8px Arial, sans-serif';
    ctx.fillStyle = '#64748b';
    ctx.fillText('★ EXCLUSIVO MIEMBRO DE LA RED ★', 270, 655);

    const dataUrl = canvas.toDataURL('image/webp', 0.85);
    canvas.toBlob((b) => {
      resolve({ blob: b!, dataUrl });
    }, 'image/webp', 0.85);
  });
};

export default function GravityScreen({ onRedirectToMuro }: { onRedirectToMuro?: () => void }) {
  try {
    return <GravityScreenInner onRedirectToMuro={onRedirectToMuro} />;
  } catch (err) {
    console.error("GRAVITY_SCREEN_CRASH:", err);
    return <div style={{padding: 50, color: 'white', background: 'red'}}>GRAVITY ERROR: {String(err)}</div>;
  }
}

function GravityScreenInner({ onRedirectToMuro }: { onRedirectToMuro?: () => void }) {
  // Estados de Telemetría Instantánea
  const [speed, setSpeed] = useState<number>(0);
  const [altitude, setAltitude] = useState<number | null>(null);
  const [roll, setRoll] = useState<number>(0); // Inclinación izquierda/derecha
  const [pitch, setPitch] = useState<number>(0); // Cabeceo adelante/atrás
  const [gForce, setGForce] = useState<number>(1.0); // Fuerza G actual

  // Estados de Grabación de Sesión
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [sessionVelMax, setSessionVelMax] = useState<number>(0);
  const [sessionMaxIzq, setSessionMaxIzq] = useState<number>(0);
  const [sessionMaxDer, setSessionMaxDer] = useState<number>(0);
  const [sessionMaxG, setSessionMaxG] = useState<number>(1.0);
  const [startAltitude, setStartAltitude] = useState<number | null>(null);
  const [sessionDescenso, setSessionDescenso] = useState<number>(0);
  const [sessionDistancia, setSessionDistancia] = useState<number>(0); // en kilómetros

  // Modal de Resumen y Publicación
  const [showSummaryModal, setShowSummaryModal] = useState<boolean>(false);
  const [lastSessionData, setLastSessionData] = useState<RideSession | null>(null);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);

  const rankCfg = getRankConfig(lastSessionData);

  // Historial de Sesiones Guardadas
  const [savedSessions, setSavedSessions] = useState<RideSession[]>(() => {
    try {
      const saved = Storage.get('gravity_sesiones');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [orientationPermissionGranted, setOrientationPermissionGranted] = useState<boolean | null>(null);
  const [showPermissionPrompt, setShowPermissionPrompt] = useState<boolean>(false);

  // Referencias para suscripciones de hardware y evitar cierres de estado de React (Stale Closures)
  const watchIdRef = useRef<number | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const lastCoordRef = useRef<{ lat: number; lng: number } | null>(null);
  const summaryCardRef = useRef<HTMLDivElement>(null);

  const isRecordingRef = useRef<boolean>(false);
  const startAltitudeRef = useRef<number | null>(null);

  useEffect(() => {
    isRecordingRef.current = isRecording;
    if (!isRecording) {
      lastCoordRef.current = null;
    }
  }, [isRecording]);

  useEffect(() => {
    startAltitudeRef.current = startAltitude;
  }, [startAltitude]);

  // --- SOLICITUD DE PERMISOS PARA DISPOSITIVOS ---
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      typeof (window as any).DeviceOrientationEvent !== 'undefined' &&
      typeof (window as any).DeviceOrientationEvent.requestPermission === 'function'
    ) {
      setOrientationPermissionGranted(false);
      setShowPermissionPrompt(true);
    } else {
      setOrientationPermissionGranted(true);
      registerHardwareListeners();
    }

    return () => {
      stopHardwareListeners();
    };
  }, []);

  const requestOrientationPermission = async () => {
    try {
      const DeviceOrientationEventClass = (window as any).DeviceOrientationEvent;
      if (DeviceOrientationEventClass && typeof DeviceOrientationEventClass.requestPermission === 'function') {
        const permission = await DeviceOrientationEventClass.requestPermission();
        if (permission === 'granted') {
          setOrientationPermissionGranted(true);
          setShowPermissionPrompt(false);
          registerHardwareListeners();
        } else {
          alert("Permiso denegado. No se podrá medir la inclinación lateral en curvas.");
        }
      } else {
        setOrientationPermissionGranted(true);
        setShowPermissionPrompt(false);
        registerHardwareListeners();
      }
    } catch (e) {
      console.error("Error al solicitar permisos de sensores:", e);
    }
  };

  // --- REGISTRO DE SENSORES HARDWARE NATIVOS ($0 COST) ---
  const registerHardwareListeners = () => {
    if ('geolocation' in navigator) {
      watchIdRef.current = navigator.geolocation.watchPosition(
        (position) => {
          const rawSpeed = position.coords.speed !== null && position.coords.speed >= 0 ? position.coords.speed : 0;
          const speedKmH = Math.round(rawSpeed * 3.6);
          setSpeed(speedKmH);

          const currentAlt = position.coords.altitude;
          setAltitude(currentAlt);

          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          // Actualizar métricas acumuladas de la sesión activa
          if (isRecordingRef.current) {
            setSessionVelMax((prev) => Math.max(prev, speedKmH));
            
            if (currentAlt !== null) {
              const startAltVal = startAltitudeRef.current;
              if (startAltVal === null) {
                setStartAltitude(currentAlt);
              } else {
                const drop = Math.max(0, Math.round(startAltVal - currentAlt));
                setSessionDescenso(drop);
              }
            }

            // Cálculo de distancia recorrida acumulada por GPS
            if (lastCoordRef.current === null) {
              lastCoordRef.current = { lat, lng };
            } else {
              const dist = getDistance(
                lastCoordRef.current.lat,
                lastCoordRef.current.lng,
                lat,
                lng
              );
              // Filtro anti-ruido del GPS: Ignorar saltos menores a 2 metros
              if (dist > 0.002) {
                setSessionDistancia((prev) => prev + dist);
                lastCoordRef.current = { lat, lng }; // Solo actualizamos la coordenada base cuando hay avance real
              }
            }
          }
        },
        (error) => {
          console.error("Error de GPS:", error);
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
    }

    window.addEventListener('deviceorientation', handleOrientation);
    window.addEventListener('devicemotion', handleMotion);
  };

  const stopHardwareListeners = () => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    window.removeEventListener('deviceorientation', handleOrientation);
    window.removeEventListener('devicemotion', handleMotion);
  };

  const handleOrientation = (event: DeviceOrientationEvent) => {
    const currentRoll = event.gamma !== null ? Math.round(event.gamma) : 0;
    const currentPitch = event.beta !== null ? Math.round(event.beta) : 0;

    setRoll(currentRoll);
    setPitch(currentPitch);

    if (isRecordingRef.current) {
      if (currentRoll < 0) {
        setSessionMaxIzq((prev) => Math.max(prev, Math.abs(currentRoll)));
      } else {
        setSessionMaxDer((prev) => Math.max(prev, currentRoll));
      }
    }
  };

  const handleMotion = (event: DeviceMotionEvent) => {
    const acc = event.accelerationIncludingGravity;
    if (acc) {
      const x = acc.x || 0;
      const y = acc.y || 0;
      const z = acc.z || 0;
      const magnitude = Math.sqrt(x * x + y * y + z * z);
      const currentG = parseFloat((magnitude / 9.80665).toFixed(2));
      
      setGForce(currentG);

      if (isRecordingRef.current) {
        setSessionMaxG((prev) => Math.max(prev, currentG));
      }
    }
  };

  // --- MANEJO DE GRABACIÓN ---
  const startRecording = () => {
    setIsRecording(true);
    setStartTime(Date.now());
    setElapsedTime(0);
    setSessionVelMax(0);
    setSessionMaxIzq(0);
    setSessionMaxDer(0);
    setSessionMaxG(1.0);
    setSessionDescenso(0);
    setSessionDistancia(0);
    lastCoordRef.current = null;
    
    if (altitude !== null) {
      setStartAltitude(altitude);
    } else {
      setStartAltitude(null);
    }

    timerRef.current = setInterval(() => {
      setElapsedTime((prev) => prev + 1);
    }, 1000);
  };

  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRecording(false);

    const formatTime = (secs: number) => {
      const m = Math.floor(secs / 60).toString().padStart(2, '0');
      const s = (secs % 60).toString().padStart(2, '0');
      return `${m}:${s}`;
    };

    const newSession: RideSession = {
      id: Date.now().toString(),
      fecha: new Date().toLocaleDateString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }),
      tiempo: formatTime(elapsedTime),
      velMax: sessionVelMax || speed || 0,
      tumbadaMaxIzq: sessionMaxIzq || 0,
      tumbadaMaxDer: sessionMaxDer || 0,
      gForceMax: sessionMaxG || 1.0,
      descensoMetros: sessionDescenso || 0,
      distancia: parseFloat(sessionDistancia.toFixed(2))
    };

    const updated = [newSession, ...savedSessions].slice(0, 20);
    setSavedSessions(updated);
    Storage.set('gravity_sesiones', JSON.stringify(updated));
    setStartTime(null);
    setLastSessionData(newSession);
    setShowSummaryModal(true); // Desplegar Popup de Celebración Premium

    // Sincronizar kilómetros con Supabase y el ranking de Parche
    if (sessionDistancia > 0) {
      saveKilometersToSupabase(sessionDistancia);
    }
  };

  // Sincronización en caliente del odómetro con la bicicleta del usuario en Supabase
  const saveKilometersToSupabase = async (kmToAdd: number) => {
    try {
      const userId = Storage.get('gravity_user_id');
      if (!userId) return;

      const userStr = Storage.get('gravity_user');
      const userObj = userStr ? JSON.parse(userStr) : null;
      const userPin = userObj?.pin || '';

      if (userPin) {
        // Llamar directamente a la RPC segura, que busca el vehículo e incrementa los kilómetros
        const { data: rpcData, error: rpcError } = await supabase.rpc('sincronizar_kilometros_gravity_seguro', {
          p_usuario_id: userId,
          p_pin: userPin,
          p_km_a_sumar: kmToAdd
        });

        if (!rpcError && rpcData && rpcData.success) {
          console.log("Kilómetros sincronizados con éxito usando la RPC segura:", rpcData);
          
          // Sincronizar el caché de 'gravity_metricas' para que aparezca al instante en ParcheScreen
          const finalVehId = rpcData.vehiculo_id;
          const finalKm = rpcData.km_actual;
          if (finalVehId) {
            const metricasStr = Storage.get('gravity_metricas');
            const metricasObj = metricasStr ? JSON.parse(metricasStr) : {};
            metricasObj[finalVehId] = { km: finalKm };
            Storage.set('gravity_metricas', JSON.stringify(metricasObj));
          }
          return;
        } else {
          console.warn("Fallo al llamar la RPC segura, ejecutando plan de fallback clásico:", rpcError || rpcData?.error);
        }
      }

      // FALLBACK: Si no hay PIN o falla la RPC, usamos el método directo clásico (por si acaso)
      const { data: veh } = await supabase
        .from('vehiculos')
        .select('id, km_actual')
        .eq('usuario_id', userId)
        .limit(1)
        .maybeSingle();

      let finalVehId = '';
      let finalKm = 0;

      if (veh) {
        finalVehId = veh.id;
        finalKm = Number(veh.km_actual || 0) + kmToAdd;
        await supabase
          .from('vehiculos')
          .update({ km_actual: finalKm })
          .eq('id', veh.id);
      } else {
        // Generar bicicleta por defecto si no tiene, para que no rompa el ranking del Parche
        const { data: newVeh } = await supabase
          .from('vehiculos')
          .insert([{ usuario_id: userId, apodo: 'Bicicleta Gravity', km_actual: kmToAdd }])
          .select()
          .single();
        if (newVeh) {
          finalVehId = newVeh.id;
          finalKm = kmToAdd;
        }
      }

      // Sincronizar el caché de 'gravity_metricas'
      if (finalVehId) {
        const metricasStr = Storage.get('gravity_metricas');
        const metricasObj = metricasStr ? JSON.parse(metricasStr) : {};
        metricasObj[finalVehId] = { km: finalKm };
        Storage.set('gravity_metricas', JSON.stringify(metricasObj));
      }
    } catch (err) {
      console.error("Error al guardar kilómetros en Supabase:", err);
    }
  };

  // Captura de pantalla y publicación automatizada al Muro Bike con Canvas de alta resolución
  const publishToMuroBike = async () => {
    if (!lastSessionData || isPublishing) return;
    setIsPublishing(true);

    try {
      const { blob, dataUrl } = await generateTelemetryImage(lastSessionData, rankCfg);
      if (!blob) throw new Error("Fallo al empaquetar la captura de telemetría.");

      // 4. Intentar subir imagen al Bucket 'muro_biker' de Supabase Storage
      const fileName = `gravity-descent-${Date.now()}-${Math.floor(Math.random() * 1000)}.webp`;
      const filePath = `posts/${fileName}`;
      let finalImageUrl = '';

      try {
        const { error: uploadError } = await supabase.storage
          .from('muro_biker')
          .upload(filePath, blob, { contentType: 'image/webp' });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('muro_biker')
          .getPublicUrl(filePath);

        finalImageUrl = publicUrl;
      } catch (uploadErr) {
        console.warn("Fallo de subida a Storage (restricción de RLS/Sesión). Aplicando auto-curación con fallback de Base64...", uploadErr);
        // Fallback auto-curativo: Almacenar directamente la cadena Base64
        finalImageUrl = dataUrl;
      }

      // 5. Insertar post oficial de Gravity en el Muro Biker mediante el RPC Seguro
      const userStr = Storage.get('gravity_user');
      const user = userStr ? JSON.parse(userStr) : {};
      const pin = user.pin;
      const userId = Storage.get('gravity_user_id');

      if (!pin) throw new Error("PIN de seguridad no disponible. Inicia sesión de nuevo.");

      const description = `🚀 ¡Acabo de conquistar una bajada con Gravity Bike logrando Rango ${rankCfg.rankName}! 🚴‍♂️💨 Récord de telemetría en tiempo real (IRG: ${rankCfg.irg} pts) registrado en el Cockpit. ¡Supérame si puedes! 🏁🏆`;

      const { error: rpcError } = await supabase.rpc('publicar_en_muro_gravity_seguro', {
        p_usuario_id: userId,
        p_pin: pin,
        p_descripcion: description,
        p_fotos: [finalImageUrl]
      });

      if (rpcError) throw rpcError;

      alert("🎉 ¡CONQUISTA PUBLICADA! Tu récord y captura de telemetría en tiempo real se han publicado con éxito en el Muro Bike de la comunidad.");
      setShowSummaryModal(false);
      onRedirectToMuro?.();
    } catch (err: any) {
      console.error("Error al publicar en Muro Bike:", err);
      alert(`Error al publicar: ${err.message || err}`);
    } finally {
      setIsPublishing(false);
    }
  };

  const deleteSession = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm("¿Seguro que deseas eliminar esta sesión de bajada de tu historial?")) {
      const updated = savedSessions.filter((s) => s.id !== id);
      setSavedSessions(updated);
      Storage.set('gravity_sesiones', JSON.stringify(updated));
    }
  };

  const formatTimer = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  // Re-conectar sensores al cambiar estado de grabación
  useEffect(() => {
    if (orientationPermissionGranted) {
      stopHardwareListeners();
      registerHardwareListeners();
    }
    return () => {
      stopHardwareListeners();
    };
  }, [isRecording, startAltitude]);

  return (
    <div className="flex flex-col gap-4 w-full text-white">
      {/* PROMPT DE PERMISO SENSOR (Para iPhones / iOS) */}
      {showPermissionPrompt && (
        <div className="bg-primary-cyan/10 border-2 border-primary-cyan/30 rounded-2xl p-4 flex flex-col gap-3">
          <p className="text-xs text-slate-300 font-semibold leading-relaxed">
            <Info className="w-4 h-4 text-primary-cyan inline mr-1.5 align-text-bottom" />
            Para medir con total precisión tu ángulo de tumbada en curvas de Gravity Bike, necesitamos habilitar el giroscopio de tu teléfono.
          </p>
          <button
            onClick={requestOrientationPermission}
            className="w-full py-2 bg-primary-cyan text-black font-black text-xs uppercase tracking-widest rounded-xl hover:bg-white active:scale-95 transition-all"
          >
            Habilitar Giroscopio
          </button>
        </div>
      )}

      {/* CONTENEDOR CENTRAL: VELOCÍMETRO NEGRO PREMIUM */}
      <div 
        className="relative bg-black border-[4px] border-[#0B0F19] rounded-[28px] p-5 shadow-[0_15px_40px_rgba(0,0,0,0.8)] overflow-hidden flex flex-col items-center justify-center"
      >
        <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-primary-cyan to-transparent opacity-60 z-10"></div>
        <div className="absolute -top-24 -left-24 w-48 h-48 bg-primary-cyan/5 rounded-full blur-3xl pointer-events-none z-10"></div>
        
        {/* Cronómetro de Bajada (En flujo normal, sin absolute para evitar sobreponerse al velocímetro) */}
        <div className="h-9 flex items-center justify-center mb-3 relative z-10">
          <AnimatePresence>
            {isRecording && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-red-500/10 border border-red-500/30 px-4 py-1.5 rounded-full flex items-center gap-2"
              >
                <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-ping"></div>
                <span className="text-xs font-black tracking-widest text-red-400 font-mono">
                  {formatTimer(elapsedTime)}
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Círculo Velocímetro */}
        <div className="w-40 h-40 rounded-full border-4 border-dashed border-primary-cyan/20 flex flex-col items-center justify-center relative z-10 mt-2 mb-6 shadow-[0_0_50px_rgba(0,168,255,0.05)]">
          <div className="absolute inset-4 rounded-full bg-primary-cyan/[0.02] blur-md"></div>


          {/* Aguja de inclinación visual interactiva */}
          <div
            className="absolute inset-0 rounded-full border-t-4 border-primary-cyan transition-transform duration-100 pointer-events-none"
            style={{ transform: `rotate(${isRecording ? roll : 0}deg)` }}
          ></div>

          {/* Valor de Velocidad */}
          <span className="text-6xl font-black text-yellow-400 tracking-tighter drop-shadow-[0_4px_12px_rgba(250,204,21,0.25)] leading-none">
            {isRecording ? speed : 0}
          </span>
          <span className="text-[10px] font-black text-primary-cyan tracking-[0.25em] uppercase mt-1">
            KM/H
          </span>

          {/* Altitud Actual */}
          {altitude !== null && (
            <span className="text-[9px] font-bold text-slate-400 mt-1 flex items-center gap-1 uppercase tracking-wider">
              <TrendingDown className="w-3.5 h-3.5 text-primary-cyan" /> {Math.round(altitude)} m
            </span>
          )}
        </div>

        {/* BOTÓN REGISTRO DE CARRERA / BAJADA */}
        <button
          onClick={isRecording ? stopRecording : startRecording}
          className={`w-full max-w-xs py-2 px-5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-3 active:scale-95 shadow-lg border relative overflow-hidden z-10 ${
            isRecording
              ? 'bg-red-500/10 hover:bg-red-500/20 border-red-500/40 text-red-400 shadow-[0_0_20px_rgba(239,68,68,0.15)]'
              : 'bg-yellow-400 text-black hover:bg-white border-transparent shadow-[0_10px_25px_rgba(250,204,21,0.35)]'
          }`}
        >
          {isRecording ? (
            <>
              <Square className="w-4 h-4 fill-current" />
              <span>Finalizar Descenso</span>
            </>
          ) : (
            <>
              <Play className="w-4 h-4 fill-current animate-pulse" />
              <span>Grabar Sesión de Bajada</span>
            </>
          )}
        </button>
      </div>

      {/* GRILLA DE SENSORES EN TIEMPO REAL ($0 COST) */}
      <div className="grid grid-cols-2 gap-4">
        {/* PANEL INCLINACIÓN LATERAL (ROLL) */}
        <div className="bg-[#0b0f19] border border-white/5 rounded-3xl py-3 px-4 flex flex-col items-center text-center relative overflow-hidden">
          <div className="w-8 h-8 bg-primary-cyan/10 rounded-full flex items-center justify-center mb-1.5 border border-primary-cyan/20">
            <Compass className="w-4 h-4 text-primary-cyan" />
          </div>
          <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">
            Inclinación (Roll)
          </span>
          <span className="text-2xl font-black text-white mt-1 drop-shadow-md">
            {isRecording ? Math.abs(roll) : 0}°
          </span>
          <span className="text-[9px] font-bold text-primary-cyan/80 mt-1 uppercase tracking-wider">
            {!isRecording ? 'INACTIVO' : roll < 0 ? '← Izquierda' : roll > 0 ? 'Derecha →' : 'Centrado'}
          </span>

          {/* Representación Gráfica de Inclinación */}
          <div className="w-16 h-1.5 bg-slate-800 rounded-full mt-1.5 overflow-hidden relative">
            <div
              className={`h-full absolute top-0 rounded-full transition-all duration-100 ${
                !isRecording ? 'bg-slate-700 left-1/2' : roll < 0 ? 'bg-red-500 right-1/2' : 'bg-green-500 left-1/2'
              }`}
              style={{ width: `${isRecording ? Math.min(50, (Math.abs(roll) / 90) * 50) : 0}%` }}
            ></div>
          </div>
        </div>

        {/* PANEL FUERZAS G */}
        <div className="bg-[#0b0f19] border border-white/5 rounded-3xl py-3 px-4 flex flex-col items-center text-center relative overflow-hidden">
          <div className="w-8 h-8 bg-amber-500/10 rounded-full flex items-center justify-center mb-1.5 border border-amber-500/20">
            <Zap className="w-4 h-4 text-amber-500 animate-pulse" />
          </div>
          <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">
            Fuerza Gravitatoria G
          </span>
          <span className="text-2xl font-black text-white mt-1 drop-shadow-md">
            {isRecording ? gForce : '0.0'} G
          </span>
          <span className="text-[9px] font-bold text-amber-500 mt-1 uppercase tracking-wider">
            {!isRecording ? 'INACTIVO' : gForce > 1.2 ? '🔥 ¡Sintiendo G-Force!' : 'Estable'}
          </span>

          {/* Esfera de Fuerzas G */}
          <div className="w-10 h-10 rounded-full border border-white/10 mt-1 flex items-center justify-center relative">
            <div className="w-1.5 h-1.5 bg-white/20 rounded-full absolute"></div>
            <motion.div
              className="w-2 h-2 bg-amber-500 rounded-full absolute"
              animate={{
                x: isRecording ? Math.max(-15, Math.min(15, roll / 3)) : 0,
                y: isRecording ? Math.max(-15, Math.min(15, pitch / 3)) : 0
              }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            ></motion.div>
          </div>
        </div>
      </div>

      {/* METRICAS DE LA SESIÓN EN CURSO (SOLO MIENTRAS GRABA) */}
      <AnimatePresence>
        {isRecording && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-gradient-to-r from-red-950/20 to-[#0b0f19] border border-red-500/20 rounded-3xl p-5 relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-1.5 h-full bg-red-500"></div>
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5 text-red-400 animate-pulse" />
              <h3 className="text-xs font-black text-white uppercase tracking-widest">
                Métricas Grabadas en Tiempo Real
              </h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col">
                <span className="text-[9px] text-slate-500 font-bold uppercase tracking-wider">
                  Velocidad Máxima
                </span>
                <span className="text-lg font-black text-white">{sessionVelMax} km/h</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[9px] text-slate-500 font-bold uppercase tracking-wider">
                  Metros Descendidos
                </span>
                <span className="text-lg font-black text-white">-{sessionDescenso} metros</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[9px] text-slate-500 font-bold uppercase tracking-wider">
                  Tumbada Máx Izq
                </span>
                <span className="text-lg font-black text-red-400">{sessionMaxIzq}°</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[9px] text-slate-500 font-bold uppercase tracking-wider">
                  Tumbada Máx Der
                </span>
                <span className="text-lg font-black text-green-400">{sessionMaxDer}°</span>
              </div>
              <div className="flex flex-col col-span-2 mt-2 pt-2 border-t border-white/5 flex-row justify-between">
                <span className="text-[9px] text-slate-500 font-bold uppercase tracking-wider flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5 text-primary-cyan animate-bounce" /> Distancia por GPS:
                </span>
                <span className="text-sm font-black text-white">{sessionDistancia.toFixed(2)} km</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* HISTORIAL DE BAJADAS */}
      <div className="flex flex-col gap-3 mt-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-primary-cyan" />
            <h3 className="text-xs font-black uppercase tracking-widest text-slate-300">
              Mis Descensos (Historial)
            </h3>
          </div>
          <span className="text-[9px] font-extrabold bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full uppercase">
            {savedSessions.length} Sesiones
          </span>
        </div>

        {savedSessions.length === 0 ? (
          <div className="bg-[#0b0f19] border border-dashed border-white/5 rounded-3xl p-8 text-center flex flex-col items-center">
            <Award className="w-10 h-10 text-slate-600 mb-3" />
            <p className="text-slate-400 text-xs font-bold uppercase tracking-widest leading-relaxed">
              No hay descensos guardados
            </p>
            <p className="text-slate-500 text-[10px] mt-1 uppercase tracking-wider max-w-[240px] leading-relaxed mx-auto">
              Presiona el botón de arriba cuando salgas a rodar para medir tus récords.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3 max-h-[350px] overflow-y-auto pr-1 scrollbar-hide">
            {savedSessions.map((session) => (
              <div
                key={session.id}
                className="bg-[#0b0f19]/80 border border-white/5 rounded-2xl p-4 flex flex-col relative group hover:border-primary-cyan/20 transition-all duration-300"
              >
                <div className="flex justify-between items-start">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-black text-primary-cyan uppercase tracking-widest">
                      {session.fecha}
                    </span>
                    <span className="text-[9px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">
                      Bajada: {session.tiempo} min
                    </span>
                  </div>
                  <button
                    onClick={(e) => deleteSession(session.id, e)}
                    className="p-1.5 text-slate-600 hover:text-red-400 rounded-lg hover:bg-white/5 transition-colors"
                    title="Eliminar sesión"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="grid grid-cols-5 gap-1 mt-3 pt-3 border-t border-white/5 text-center">
                  <div className="flex flex-col">
                    <span className="text-[7px] text-slate-500 font-bold uppercase tracking-widest">
                      V. Max
                    </span>
                    <span className="text-[10px] font-black text-white mt-0.5">
                      {session.velMax} km/h
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[7px] text-slate-500 font-bold uppercase tracking-widest">
                      Desnivel
                    </span>
                    <span className="text-[10px] font-black text-slate-300 mt-0.5">
                      -{session.descensoMetros}m
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[7px] text-slate-500 font-bold uppercase tracking-widest">
                      Dist.
                    </span>
                    <span className="text-[10px] font-black text-primary-cyan mt-0.5">
                      {session.distancia || 0} km
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[7px] text-slate-500 font-bold uppercase tracking-widest">
                      Izq Máx
                    </span>
                    <span className="text-[10px] font-black text-red-400 mt-0.5">
                      {session.tumbadaMaxIzq}°
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[7px] text-slate-500 font-bold uppercase tracking-widest">
                      Der Máx
                    </span>
                    <span className="text-[10px] font-black text-green-400 mt-0.5">
                      {session.tumbadaMaxDer}°
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODAL RESUMEN DE BAJADA CON CELEBRACIÓN Y PUBLICACIÓN AUTOMÁTICA */}
      <AnimatePresence>
        {showSummaryModal && lastSessionData && (
          <div className="fixed inset-0 z-[600] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl overflow-y-auto">
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 50 }}
              className="w-full max-w-sm bg-black border border-primary-cyan/30 rounded-[35px] p-6 shadow-[0_0_50px_rgba(0,168,255,0.3)] relative my-8"
            >
              {/* Tarjeta a Capturar con html2canvas */}
              <div
                ref={summaryCardRef}
                className={`w-full ${rankCfg.bg} p-6 rounded-3xl ${rankCfg.border} ${rankCfg.shadow} flex flex-col items-center relative overflow-hidden transition-all duration-500`}
              >
                {/* Estética de Cockpit F1 */}
                <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-red-500 via-primary-cyan to-green-500"></div>
                
                <span className={`text-[8px] font-mono ${rankCfg.accentColor} uppercase tracking-[6px] mb-1`}>
                  Gravity Telemetry
                </span>
                <h3 className={`text-xl font-black ${rankCfg.titleColor} uppercase tracking-tighter mb-1 text-center`}>
                  {rankCfg.title}
                </h3>
                
                <div className={`px-3 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest mb-5 ${rankCfg.badgeBg}`}>
                  IRG: {rankCfg.irg} PTS
                </div>

                {/* Cuadrícula de Métricas */}
                <div className="grid grid-cols-2 gap-3 w-full mb-5">
                  <div className={`bg-black/30 border ${rankCfg.accentBorder} p-3 rounded-2xl text-center shadow-inner`}>
                    <span className="text-[8px] text-slate-500 font-bold uppercase tracking-widest">
                      Velocidad Máx
                    </span>
                    <p className="text-2xl font-black text-white mt-1">
                      {lastSessionData.velMax}{' '}
                      <span className={`text-[10px] ${rankCfg.accentColor} font-mono`}>km/h</span>
                    </p>
                  </div>
                  <div className={`bg-black/30 border ${rankCfg.accentBorder} p-3 rounded-2xl text-center shadow-inner`}>
                    <span className="text-[8px] text-slate-500 font-bold uppercase tracking-widest">
                      Tiempo total
                    </span>
                    <p className="text-2xl font-black text-white mt-1">
                      {lastSessionData.tiempo}{' '}
                      <span className={`text-[10px] ${rankCfg.accentColor} font-mono`}>min</span>
                    </p>
                  </div>
                  <div className={`bg-black/30 border ${rankCfg.accentBorder} p-3 rounded-2xl text-center shadow-inner`}>
                    <span className="text-[8px] text-slate-500 font-bold uppercase tracking-widest">
                      Caída Vertical
                    </span>
                    <p className="text-2xl font-black text-slate-300 mt-1">
                      -{lastSessionData.descensoMetros}{' '}
                      <span className={`text-[10px] ${rankCfg.accentColor} font-mono`}>m</span>
                    </p>
                  </div>
                  <div className={`bg-black/30 border ${rankCfg.accentBorder} p-3 rounded-2xl text-center shadow-inner`}>
                    <span className="text-[8px] text-slate-500 font-bold uppercase tracking-widest">
                      Fuerza G Máx
                    </span>
                    <p className="text-2xl font-black text-amber-500 mt-1">
                      {lastSessionData.gForceMax}{' '}
                      <span className="text-[10px] text-amber-500/80 font-mono">G</span>
                    </p>
                  </div>
                </div>

                {/* Balance de Tumbada Lateral (Curvas) */}
                <div className={`w-full bg-black/30 border ${rankCfg.accentBorder} rounded-2xl p-4 text-center mb-1`}>
                  <span className="text-[8px] text-slate-500 font-bold uppercase tracking-widest">
                    Balance de Tumbada (Roll)
                  </span>
                  
                  <div className="flex justify-between items-center mt-2 px-1">
                    <div className="flex flex-col">
                      <span className="text-[8px] text-red-400 font-bold uppercase">Izq</span>
                      <span className="text-xs font-black text-white">{lastSessionData.tumbadaMaxIzq}°</span>
                    </div>

                    {/* Barra de balance de inclinación */}
                    <div className="flex-1 h-1.5 bg-slate-800 rounded-full mx-3 relative overflow-hidden">
                      <div
                        className={`absolute h-full ${rankCfg.barColorLeft} right-1/2 rounded-l-full`}
                        style={{ width: `${Math.min(50, (lastSessionData.tumbadaMaxIzq / 90) * 50)}%` }}
                      ></div>
                      <div
                        className={`absolute h-full ${rankCfg.barColorRight} left-1/2 rounded-r-full`}
                        style={{ width: `${Math.min(50, (lastSessionData.tumbadaMaxDer / 90) * 50)}%` }}
                      ></div>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-[8px] text-green-400 font-bold uppercase">Der</span>
                      <span className="text-xs font-black text-white">{lastSessionData.tumbadaMaxDer}°</span>
                    </div>
                  </div>

                  {/* Kilometraje de la Bajada */}
                  <div className="mt-4 pt-3 border-t border-white/5 flex justify-between items-center px-1">
                    <span className="text-[8px] text-slate-400 font-black uppercase tracking-wider flex items-center gap-1">
                      <MapPin className={`w-3.5 h-3.5 ${rankCfg.accentColor}`} /> Distancia GPS:
                    </span>
                    <span className="text-xs font-mono font-black text-white">
                      {lastSessionData.distancia || 0} km
                    </span>
                  </div>
                </div>
              </div>

              {/* Botones de Acción Fuera de la Captura */}
              <div className="flex flex-col gap-3 mt-6">
                <button
                  onClick={publishToMuroBike}
                  disabled={isPublishing || rankCfg.irg === 0}
                  className={`w-full py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg ${
                    rankCfg.irg === 0
                      ? 'bg-[#121620] text-slate-500 border border-slate-800 cursor-not-allowed shadow-none'
                      : 'bg-primary-cyan text-black hover:bg-white active:scale-95 shadow-primary-cyan/20 cursor-pointer'
                  } disabled:opacity-50`}
                >
                  {isPublishing ? (
                    <>
                      <RotateCcw className="w-4 h-4 animate-spin" />
                      <span>Subiendo captura...</span>
                    </>
                  ) : rankCfg.irg === 0 ? (
                    <>
                      <Info className="w-4 h-4 text-slate-500" />
                      <span>Mínimo 1 punto para publicar</span>
                    </>
                  ) : (
                    <>
                      <Camera className="w-4 h-4" />
                      <span>Publicar Récord en Muro Bike</span>
                    </>
                  )}
                </button>

                <button
                  onClick={() => setShowSummaryModal(false)}
                  disabled={isPublishing}
                  className="w-full py-3 bg-white/5 border border-white/10 hover:bg-white/10 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all active:scale-95 disabled:opacity-30"
                >
                  Cerrar y Volver
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
